import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import authApi from '../../services/api/auth.api';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const user = useSelector((state: any) => state.auth.user);

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!sessionId || !user) {
      setStatus('error');
      setErrorMessage('Invalid session or user not logged in');
      setTimeout(() => navigate('/register'), 3000);
      return;
    }

    completeSetup();
  }, [sessionId, user]);

  const completeSetup = async () => {
    try {
      const response = await authApi.completePaymentSetup({
        sessionId: sessionId!,
        tenantId: user.tenantId,
      });

      if (response.success) {
        setStatus('success');
        // Redirect to dashboard after 2 seconds
        setTimeout(() => navigate('/dashboard'), 2000);
      } else {
        setStatus('error');
        setErrorMessage('Failed to complete payment setup');
      }
    } catch (error: any) {
      console.error('Error completing payment setup:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.error || 'Failed to complete payment setup');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 text-indigo-600">
            <ChartBarIcon className="h-12 w-12" />
            <span className="text-3xl font-bold">Kubmix Flux</span>
          </div>
        </div>

        {/* Status Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
          {status === 'loading' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Processing Payment Setup
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Please wait while we complete your registration...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
                <CheckCircleIcon className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Payment Setup Complete!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Your account is now ready. Redirecting to dashboard...
              </p>
              <div className="flex items-center gap-2 text-sm text-indigo-600 dark:text-indigo-400 justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600"></div>
                <span>Taking you to your dashboard</span>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                <ExclamationCircleIcon className="h-10 w-10 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Setup Failed
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {errorMessage || 'There was an error completing your payment setup.'}
              </p>
              <button
                onClick={() => navigate('/register')}
                className="w-full px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Help Text */}
        <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
          Need help?{' '}
          <a href="mailto:support@devskin.io" className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
}
